import React, { useState } from 'react';
import { PersonCard } from './PersonCard';
import { FilterBar } from './FilterBar';
import { peopleProfiles } from '../utils/mockData';
import { UsersIcon, FilterIcon, SlidersIcon, AlertCircleIcon, SearchIcon, TagIcon, XIcon, CheckIcon } from 'lucide-react';
import { PersonModal } from './PersonModal';
import { AdvancedFilters } from './AdvancedFilters';
interface PeopleProps {
  onPersonSelect: (personId: number) => void;
  selectedPersonId: number | null;
  onMarkInteresting: (personId: number) => void;
  onMarkNotRelevant: (personId: number) => void;
  interestingPeople: number[];
  notRelevantPeople: number[];
}
export const People: React.FC<PeopleProps> = ({
  onPersonSelect,
  selectedPersonId,
  onMarkInteresting,
  onMarkNotRelevant,
  interestingPeople,
  notRelevantPeople
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState('interesting');
  const [showPersonModal, setShowPersonModal] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [sortOrder, setSortOrder] = useState<string>('relevance');
  const [advancedFilters, setAdvancedFilters] = useState<any>({});
  const [recentAction, setRecentAction] = useState<{
    id: number;
    action: 'interesting' | 'not-relevant';
  } | null>(null);
  const allTags = Array.from(new Set(peopleProfiles.flatMap(person => person.tags)));
  const handlePersonClick = (personId: number) => {
    onPersonSelect(personId);
    setShowPersonModal(true);
  };
  const handleClosePersonModal = () => {
    setShowPersonModal(false);
  };
  const handleNavigateToMessages = (personId: number) => {
    onPersonSelect(personId);
    // This would normally navigate to messages tab, but we'll handle that in the parent component
  };
  const handleOpenAdvancedFilters = () => {
    setShowAdvancedFilters(true);
  };
  const handleCloseAdvancedFilters = () => {
    setShowAdvancedFilters(false);
  };
  const handleApplyAdvancedFilters = (filters: any) => {
    setAdvancedFilters(filters);
  };
  const handleMarkAction = (personId: number, action: 'interesting' | 'not-relevant') => {
    if (action === 'interesting') {
      onMarkInteresting(personId);
    } else {
      onMarkNotRelevant(personId);
    }
    // Show the recent action for potential undo
    setRecentAction({
      id: personId,
      action
    });
    // Auto-dismiss after 5 seconds
    setTimeout(() => {
      setRecentAction(null);
    }, 5000);
  };
  const handleUndoAction = () => {
    if (!recentAction) return;
    const {
      id,
      action
    } = recentAction;
    // Reverse the action
    if (action === 'interesting') {
      onMarkNotRelevant(id);
    } else {
      onMarkInteresting(id);
    }
    setRecentAction(null);
  };
  const handleSearch = (term: string) => {
    setSearchTerm(term);
  };
  const handleTagSelect = (tag: string) => {
    setSelectedTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);
  };
  // Filter people based on current filters
  const filteredPeople = peopleProfiles.filter(person => {
    // Filter by search term
    const matchesSearch = searchTerm === '' || person.name.toLowerCase().includes(searchTerm.toLowerCase()) || person.title.toLowerCase().includes(searchTerm.toLowerCase()) || person.company.toLowerCase().includes(searchTerm.toLowerCase());
    // Filter by selected tags
    const matchesTags = selectedTags.length === 0 || selectedTags.some(tag => person.tags.includes(tag));
    // Filter by tab
    const matchesTab = activeTab === 'interesting' && interestingPeople.includes(person.id) || activeTab === 'not-relevant' && notRelevantPeople.includes(person.id) || activeTab === 'all';
    // Apply advanced filters if they exist
    let matchesAdvancedFilters = true;
    if (Object.keys(advancedFilters).length > 0) {
      if (advancedFilters.relevanceLevel?.length > 0) {
        matchesAdvancedFilters = matchesAdvancedFilters && advancedFilters.relevanceLevel.includes(person.relevance);
      }
      if (advancedFilters.engagementType?.length > 0) {
        const personEngagementTypes = person.interactions.map(i => i.type);
        matchesAdvancedFilters = matchesAdvancedFilters && advancedFilters.engagementType.some((type: string) => {
          if (type === 'multiple') {
            return person.interactions.length > 1;
          }
          return personEngagementTypes.includes(type);
        });
      }
      if (advancedFilters.dateRange && advancedFilters.dateRange !== 'all') {
        // Simplified date filtering for demo
        const hasRecentInteraction = person.interactions.some(i => i.date.includes('day') && parseInt(i.date) <= (advancedFilters.dateRange === 'week' ? 7 : advancedFilters.dateRange === 'month' ? 30 : 90));
        matchesAdvancedFilters = matchesAdvancedFilters && hasRecentInteraction;
      }
    }
    return matchesSearch && matchesTags && matchesTab && matchesAdvancedFilters;
  });
  // Sort the filtered people
  const sortedPeople = [...filteredPeople].sort((a, b) => {
    if (sortOrder === 'relevance') {
      const relevanceOrder = {
        high: 3,
        medium: 2,
        low: 1
      };
      return relevanceOrder[b.relevance as keyof typeof relevanceOrder] - relevanceOrder[a.relevance as keyof typeof relevanceOrder];
    } else if (sortOrder === 'recent') {
      // Sort by most recent interaction
      const getLatestDate = (person: (typeof peopleProfiles)[0]) => {
        if (person.interactions.length === 0) return 0;
        const dates = person.interactions.map(i => {
          if (i.date.includes('day')) {
            return parseInt(i.date) || 0;
          }
          if (i.date.includes('week')) {
            return parseInt(i.date) * 7 || 0;
          }
          return 30; // for "month" or any other unit
        });
        return Math.min(...dates);
      };
      return getLatestDate(a) - getLatestDate(b);
    } else if (sortOrder === 'name') {
      return a.name.localeCompare(b.name);
    }
    return 0;
  });
  return <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-id="element-784">
      {/* Undo action notification */}
      {recentAction && <div className="fixed bottom-4 right-4 bg-gray-800 text-white px-4 py-3 rounded-lg shadow-lg z-50 flex items-center" data-id="element-785">
          <div className="mr-3" data-id="element-786">
            {recentAction.action === 'interesting' ? <CheckIcon size={16} className="text-green-400" data-id="element-787" /> : <XIcon size={16} className="text-red-400" data-id="element-788" />}
          </div>
          <div className="mr-4" data-id="element-789">
            <p className="text-sm font-medium" data-id="element-790">
              Contact marked as{' '}
              {recentAction.action === 'interesting' ? 'interesting' : 'not relevant'}
            </p>
          </div>
          <button onClick={handleUndoAction} className="text-sm font-medium text-blue-300 hover:text-blue-200" data-id="element-791">
            Undo
          </button>
        </div>}

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-3 sm:gap-0" data-id="element-792">
        <div data-id="element-793">
          <h1 className="text-2xl font-semibold text-gray-900" data-id="element-794">People</h1>
          <p className="text-gray-500 text-sm mt-1" data-id="element-795">
            Identify and engage with high-intent prospects
          </p>
        </div>
        {/* Simple search for mobile */}
        <div className="w-full sm:w-auto flex sm:hidden" data-id="element-796">
          <div className="relative flex-grow" data-id="element-797">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-798">
              <SearchIcon size={16} className="text-gray-400" data-id="element-799" />
            </div>
            <input type="text" placeholder="Search people..." value={searchTerm} onChange={e => handleSearch(e.target.value)} className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-[#f9b30f] focus:border-[#f9b30f] sm:text-sm" data-id="element-800" />
          </div>
        </div>
      </div>

      <div className="mb-4 overflow-x-auto" data-id="element-801">
        <div className="border-b border-gray-200 min-w-max" data-id="element-802">
          <nav className="-mb-px flex space-x-8" data-id="element-803">
            <button onClick={() => setActiveTab('interesting')} className={`whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'interesting' ? 'border-[#f9b30f] text-[#f9b30f]' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-804">
              Interesting ({interestingPeople.length})
            </button>
            <button onClick={() => setActiveTab('not-relevant')} className={`whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'not-relevant' ? 'border-[#f9b30f] text-[#f9b30f]' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-805">
              Not Relevant ({notRelevantPeople.length})
            </button>
            <button onClick={() => setActiveTab('all')} className={`whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'all' ? 'border-[#f9b30f] text-[#f9b30f]' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-806">
              All Profiles
            </button>
          </nav>
        </div>
      </div>

      {/* Desktop search and filter bar */}
      <div className="hidden sm:block mb-4" data-id="element-807">
        <FilterBar onSearch={handleSearch} selectedTags={selectedTags} onTagSelect={handleTagSelect} availableTags={allTags} data-id="element-808" />
      </div>

      {/* Mobile tag filters */}
      <div className="sm:hidden mb-4" data-id="element-809">
        <div className="flex flex-wrap gap-2" data-id="element-810">
          {selectedTags.map(tag => <span key={tag} onClick={() => handleTagSelect(tag)} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 cursor-pointer" data-id="element-811">
              <TagIcon size={12} className="mr-1" data-id="element-812" />
              {tag}
              <XIcon size={12} className="ml-1" data-id="element-813" />
            </span>)}
          <button onClick={() => setShowAdvancedFilters(true)} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-814">
            <FilterIcon size={12} className="mr-1" data-id="element-815" />
            Filters
          </button>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3" data-id="element-816">
        <div className="text-sm text-gray-500" data-id="element-817">
          {sortedPeople.length} people found
        </div>
        <div className="flex flex-wrap items-center gap-3" data-id="element-818">
          <div data-id="element-819">
            <label htmlFor="sort" className="sr-only" data-id="element-820">
              Sort by
            </label>
            <select id="sort" name="sort" value={sortOrder} onChange={e => setSortOrder(e.target.value)} className="block pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-[#f9b30f] focus:border-[#f9b30f] sm:text-sm rounded-md" data-id="element-821">
              <option value="relevance" data-id="element-822">Sort by: Relevance</option>
              <option value="recent" data-id="element-823">Sort by: Most Recent</option>
              <option value="name" data-id="element-824">Sort by: Name</option>
            </select>
          </div>
          <button onClick={handleOpenAdvancedFilters} className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-825">
            <SlidersIcon size={14} className="mr-1" data-id="element-826" />
            Advanced Filters
          </button>
        </div>
      </div>

      {/* Active filters display */}
      {Object.keys(advancedFilters).length > 0 && <div className="mb-4 p-3 bg-gray-50 rounded-md border border-gray-200 flex flex-wrap items-center justify-between gap-2" data-id="element-827">
          <div className="flex flex-wrap items-center gap-2" data-id="element-828">
            <span className="text-sm font-medium text-gray-700 mr-2" data-id="element-829">
              Active filters:
            </span>
            <div className="flex flex-wrap gap-2" data-id="element-830">
              {advancedFilters.relevanceLevel?.length > 0 && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-831">
                  Relevance: {advancedFilters.relevanceLevel.join(', ')}
                </span>}
              {advancedFilters.engagementType?.length > 0 && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-832">
                  Engagement: {advancedFilters.engagementType.join(', ')}
                </span>}
              {advancedFilters.dateRange && advancedFilters.dateRange !== 'all' && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-833">
                    Date: {advancedFilters.dateRange}
                  </span>}
            </div>
          </div>
          <button onClick={() => setAdvancedFilters({})} className="text-sm text-gray-600 hover:text-gray-900" data-id="element-834">
            Clear all
          </button>
        </div>}

      {sortedPeople.length > 0 ? <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-835">
          {sortedPeople.map(person => <PersonCard key={person.id} person={person} onSelect={handlePersonClick} isSelected={selectedPersonId === person.id} onMarkInteresting={() => handleMarkAction(person.id, 'interesting')} onMarkNotRelevant={() => handleMarkAction(person.id, 'not-relevant')} showActions={activeTab !== 'not-relevant' || activeTab === 'all'} data-id="element-836" />)}
        </div> : <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center" data-id="element-837">
          <div className="mb-4 flex justify-center" data-id="element-838">
            {activeTab === 'interesting' ? <AlertCircleIcon size={48} className="text-orange-400" data-id="element-839" /> : activeTab === 'not-relevant' ? <XIcon size={48} className="text-gray-400" data-id="element-840" /> : <UsersIcon size={48} className="text-gray-400" data-id="element-841" />}
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-1" data-id="element-842">
            {activeTab === 'interesting' ? 'No people marked as interesting yet' : activeTab === 'not-relevant' ? 'No people marked as not relevant yet' : 'No people found'}
          </h3>
          <p className="text-gray-500" data-id="element-843">
            {activeTab === 'interesting' ? "Mark people as 'Interesting' to add them here" : activeTab === 'not-relevant' ? "People you've marked as 'Not Relevant' will appear here" : 'Try adjusting your filters or search terms'}
          </p>
        </div>}

      {showPersonModal && selectedPersonId && <PersonModal personId={selectedPersonId} onClose={handleClosePersonModal} onNavigateToMessages={handleNavigateToMessages} onMarkInteresting={onMarkInteresting} onMarkNotRelevant={onMarkNotRelevant} data-id="element-844" />}

      <AdvancedFilters isOpen={showAdvancedFilters} onClose={handleCloseAdvancedFilters} onApplyFilters={handleApplyAdvancedFilters} data-id="element-845" />
    </div>;
};